/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Tenant Workspace Switcher Dropdown (Task 10.1.3 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore, type WorkspaceSummary } from '../../stores/useWorkspaceStore';
import { useUiStore } from '../../stores/useUiStore';
import { useI18n } from '../../stores/useI18nStore';
import { switchWorkspace } from '../../lib/workspace-switch';
import {
  Building2,
  ChevronDown,
  Check,
  Plus,
  User,
  Users,
} from 'lucide-react';

export const WorkspaceSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const { workspaces, activeWorkspaceId, getActiveWorkspace } = useWorkspaceStore();
  const { openModal } = useUiStore();

  const activeWorkspace = getActiveWorkspace();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelectWorkspace = (wsId: string) => {
    switchWorkspace(wsId, queryClient);
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    openModal('create_workspace');
  };

  // Group workspaces by type
  const personalWorkspaces = workspaces.filter((w) => w.type === 'personal');
  const teamWorkspaces = workspaces.filter((w) => w.type !== 'personal');

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Switcher Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated/70 hover:bg-surface-elevated text-xs font-medium text-foreground transition-all duration-150 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="w-5 h-5 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Building2 className="w-3.5 h-3.5" />
        </div>

        <span className="max-w-[120px] sm:max-w-[160px] truncate font-semibold">
          {activeWorkspace?.name ?? t('workspace.selectWorkspace')}
        </span>

        {activeWorkspace?.role && (
          <span className="hidden lg:inline-flex text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase">
            {activeWorkspace.role}
          </span>
        )}

        <ChevronDown
          className={`w-3.5 h-3.5 text-foreground-muted transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Popover Dropdown Panel */}
      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-72 rounded-2xl bg-surface border border-surface-border shadow-2xl p-2 z-50 space-y-2 animate-fade-in"
        >
          {/* Header */}
          <div className="px-3 py-1.5 border-b border-surface-border/60 flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted">
              {t('workspace.selectWorkspace')}
            </span>
            <span className="text-[10px] text-foreground-muted font-mono">
              {t('workspace.totalCount', { count: workspaces.length })}
            </span>
          </div>

          <div className="max-h-64 overflow-y-auto space-y-3 px-1 py-1">
            {/* Personal Workspaces Section */}
            {personalWorkspaces.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 px-2 text-[10px] font-semibold text-foreground-muted uppercase tracking-wider">
                  <User className="w-3 h-3" />
                  <span>{t('workspace.personal')}</span>
                </div>
                {personalWorkspaces.map((ws) => (
                  <WorkspaceItemRow
                    key={ws.id}
                    workspace={ws}
                    isActive={ws.id === activeWorkspaceId}
                    onSelect={() => handleSelectWorkspace(ws.id)}
                  />
                ))}
              </div>
            )}

            {/* Team Workspaces Section */}
            {teamWorkspaces.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 px-2 text-[10px] font-semibold text-foreground-muted uppercase tracking-wider">
                  <Users className="w-3 h-3" />
                  <span>{t('workspace.team')}</span>
                </div>
                {teamWorkspaces.map((ws) => (
                  <WorkspaceItemRow
                    key={ws.id}
                    workspace={ws}
                    isActive={ws.id === activeWorkspaceId}
                    onSelect={() => handleSelectWorkspace(ws.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Create Workspace Action Button */}
          <div className="pt-1 border-t border-surface-border/60">
            <button
              type="button"
              onClick={handleCreateNew}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('workspace.createNewWorkspace')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface WorkspaceItemRowProps {
  workspace: WorkspaceSummary;
  isActive: boolean;
  onSelect: () => void;
}

const WorkspaceItemRow: React.FC<WorkspaceItemRowProps> = ({ workspace, isActive, onSelect }) => {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      onClick={onSelect}
      className={`w-full flex items-center justify-between p-2 rounded-xl text-xs transition-colors text-left ${
        isActive
          ? 'bg-primary/10 text-primary font-semibold'
          : 'text-foreground hover:bg-muted/60'
      }`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            isActive ? 'bg-primary text-primary-fg shadow-sm' : 'bg-muted text-foreground-muted'
          }`}
        >
          {workspace.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 truncate">
          <div className="truncate font-medium">{workspace.name}</div>
          <div className="text-[10px] text-foreground-muted truncate font-mono">
            {workspace.slug}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
        {workspace.role && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-surface-border text-foreground-muted uppercase">
            {workspace.role}
          </span>
        )}
        {isActive && <Check className="w-3.5 h-3.5 text-primary" />}
      </div>
    </button>
  );
};
